import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime, formatDate } from "@utils/common";
import { useEffect, useState } from "react";
import TableWithPagination from "../Tables/TableWithPagination";
import { OutlineButton } from "@components/Buttons";
import { useTranslation } from "react-i18next";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { DocumentInfoIcon } from "@components/Icons";
import ImportedDetailsModal from '../Dialogs/ImportedDetailsModal';
import { IMPORT_MODE_LABEL } from "@constants/index";
import { IMPORT_TYPE } from "@constants/index";

const ImportHistoryModal = ({ isOpen, onClose, "data-testid": dataTestId = "import-history-modal" }) => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const [totalData, setTotalData] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [importHistoryData, setImportHistoryData] = useState([]);
    const [showImportDetailModal, setShowImportDetailModal] = useState(false);
    const [importStatusSeq, setImportStatusSeq] = useState(null);

    const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.IMPORT_HISTORY_LIST, DEFAULT_ITEMS_PER_PAGE);
    const IMPORT_STATUS_CONFIG = {
        SUCCESS: {
            label: "communication.done",
            className: colors.importStatusCreated,
        },
        IN_PROGRESS: {
            label: "preferences.running",
            className: colors.importStatusInProgress,
        },
        FAILED: {
            label: "update-summary.failed",
            className: colors.importStatusFailed,
        },
    };
    const getImportHistoryData = useMutationData(
        queryKeys.importProjectStatuses,
        {
            method: "POST",
        },
        {
            onSuccess: (response) => {
                if (typeof response !== "string") {
                    // Ensure consistent data structure for table rows
                    const data = response.data;
                    setImportHistoryData(data);
                    setTotalData(response.total || 0);
                    setTotalPages(Math.ceil((response.total || 0) / itemsPerPage));
                }
                setLoading(false);
            },
            onError: (error) => {
                console.error("Error during get data:", error);
                setLoading(false);
            },
        },
    );

    useEffect(() => {
        if (!isOpen) return;

        setLoading(true);
        getImportHistoryData.mutate({
            pageSize: itemsPerPage,
            startRow: (currentPage - 1) * itemsPerPage,
        });
    }, [isOpen, currentPage, itemsPerPage]);

    // Reset to page 1 when modal opens
    useEffect(() => {
        if (isOpen) setCurrentPage(1);
    }, [isOpen]);

    const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
        handleItemsPerPageChange(newItemsPerPage);
        setCurrentPage(1); // Reset to page 1 when changing number of rows
    };

    const renderToolScanStatusCell = (status) => {
        const config = IMPORT_STATUS_CONFIG[status];

        if (!config) return "-";

        return (
            <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${config.className}`}
            >
                {t(config.label)}
            </span>
        );
    };

    const renderDetails = (row) => {
        switch (row.importMode) {
            case IMPORT_TYPE.PROJECT_CODE: {
                const count = row.projectCodesList?.length || 0;
                const key =
                    count > 1
                        ? "preferences.project-codes-count_plural"
                        : "preferences.project-codes-count";

                return <span>{t(key, { count })}</span>;
            }

            case IMPORT_TYPE.ALL:
                return (
                    <span>
                        {t("preferences.all-available-projects")}
                    </span>
                );

            case IMPORT_TYPE.DATE_RANGE:
                return (
                    <span>
                        {t("preferences.created-from", {
                            from: formatDate(row.importFromDate),
                            to: formatDate(row.importToDate),
                        })}
                    </span>
                );

            default:
                return "-";
        }
    };

    const getImportModeLabel = (importMode) =>
    t(IMPORT_MODE_LABEL[importMode]) || "-";

    // Table columns and config
    const columns = [
        "createdAt",
        "importMode",
        "details",
        "status",
        "action",
    ];

    const columnConfig = {
        createdAt: {
            title: t("preferences.imported-date"),
            style: {
                width: "15%",
                textAlign: "left"
            },
            customRender: (value) => value ? formatDateTime(value) : "-",
            attributeTitle: false,
        },
        importMode: {
            title: t("preferences.imported-by-condition"),
            style: {
                width: "20%",
                textAlign: "left"
            },
            customRender: (value, row) => getImportModeLabel(row.importMode),
            attributeTitle: false,
        },
        details: {
            title: t("preferences.details"),
            style: {
                width: "45%",
                textAlign: "left"
            },
            customRender: (value, row) => renderDetails(row),
            attributeTitle: false,
        },
        status: {
            title: t("preferences.import-status"),
            style: {
                width: "10%",
                textAlign: "center"
            },
            customRender: (value, row) => renderToolScanStatusCell(row.status),
            attributeTitle: false,
        },
        action: {
            headerRender: () => t("preferences.action"),
            style: { width: "10%", textAlign: "center" },
            customRender: (value, row) => (
                <div className="flex justify-center items-center">
                    {row.status !== "IN_PROGRESS" && (
                        <button
                            onClick={() => {
                                setImportStatusSeq(row.importStatusSeq);
                                setShowImportDetailModal(true);

                            }}
                            className={`${colors.textNeutralMuted} ${colors.hoverTextNeutral} p-1`}
                            title={t("common.view-details")}
                        >
                            <DocumentInfoIcon size={20} />
                        </button>
                    )}
                </div>
            ),
            attributeTitle: false,
        }
    };

    if (!isOpen) return null;

    return (
        <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start md:justify-center overflow-x-auto p-4 backdrop-blur-[1px]`} data-testid={`${dataTestId}-overlay`}>
            <div
                className={`${colors.bgModal} w-[90vw] max-w-[1500px] min-w-[1150px] max-h-[95vh] flex-shrink-0 overflow-auto rounded-lg shadow-xl xl:overflow-y-hidden ${showImportDetailModal ? "opacity-0 pointer-events-none" : "opacity-100"}`}
                data-testid={dataTestId}
            >
                {/* Header */}
                <div className={`px-6 py-4`} data-testid={`${dataTestId}-header`}>
                    <div className="flex items-center justify-between">
                        <h2 className={`${colors.textPrimary} text-xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>{t("preferences.import-history")}</h2>
                    </div>
                </div>
                {/* TableWithPagination */}
                <div className="mb-4 px-6 flex flex-1 flex-col min-h-0" data-testid={`${dataTestId}-content`}>
                    <TableWithPagination
                        dataTestId={`${dataTestId}-table`}
                        columns={columns}
                        columnConfig={columnConfig}
                        data={importHistoryData}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        loading={loading}
                        rowKey="importStatusSeq"
                        tableStyle="default"
                        totalPages={totalPages}
                        totalData={totalData}
                        maxRowsPerPage={itemsPerPage}
                        onItemsPerPageChange={handleItemsPerPageChangeWithReset}
                        scrollY="650px"
                        showPagination={true}
                        stickyHeader={true}
                        showItemsPerPageSelector={true}
                    />
                </div>

                {/* Footer */}
                <div className={`px-6 py-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-footer`}>
                    <div className="flex items-center justify-end">
                        <div className="flex items-center space-x-3">
                            <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={onClose}>
                                {t("common.cancel-button")}
                            </OutlineButton>
                        </div>
                    </div>
                </div>
            </div>
            <ImportedDetailsModal
                data-testid={`${dataTestId}-details`}
                isOpen={showImportDetailModal}
                onBack={() => {
                    setShowImportDetailModal(false);
                }}
                onCancel={() => {
                    setShowImportDetailModal(false);
                    onClose();
                }}
                importStatusSeq={importStatusSeq}
            />
        </div>
    );
};

export default ImportHistoryModal; 
